/**
 * Scheme resource-loss evaluation for the Legendary Arena game engine.
 *
 * Some schemes' printed "Evil Wins" condition is a resource threshold, not a
 * twist count (D-24315). This module counts cards of a given type in the
 * Escaped Villains pile (`G.escapedPile`) and sets the SCHEME_LOSS endgame
 * counter when the active scheme's configured threshold is reached.
 *
 * Pure and side-effect-limited: it mutates only `G.counters` and appends to the
 * game log. No boardgame.io import. No registry import. No `.reduce()`.
 */
import { ENDGAME_CONDITIONS } from '../endgame/endgame.types.js';
import { BYSTANDER_EXT_ID } from '../setup/pilesInit.js';
import { SCHEME_TWIST_CONFIGS } from './schemeTwistConfigs.js';
import { pushLog } from '../log/logPush.js';
/**
 * Resolves the card type of one Escaped Villains pile entry.
 *
 * `G.villainDeckCardTypes` classifies every card built into the villain deck
 * (villains, henchmen, villain-deck bystanders, scheme twists, master strikes).
 * Bystanders captured from the shared supply via card/twist effects carry the
 * generic `BYSTANDER_EXT_ID` (`'pile-bystander'`) and are NOT in that map, so
 * they are resolved explicitly to `'bystander'` here — otherwise a scheme like
 * Midtown Bank Robbery (whose twist captures supply bystanders) would
 * undercount the bystanders carried off by escaping villains.
 *
 * @param gameState - The current game state (read-only).
 * @param escapedCardId - An ext_id present in `G.escapedPile`.
 * @returns The resolved card type, or undefined if unclassifiable.
 */
function resolveEscapedPileEntryType(gameState, escapedCardId) {
    const villainDeckType = gameState.villainDeckCardTypes[escapedCardId];
    if (villainDeckType !== undefined) {
        return villainDeckType;
    }
    // why: supply bystanders (BYSTANDER_EXT_ID) are not villain-deck cards, so
    // they carry no villainDeckCardTypes entry; classify them as 'bystander'.
    if (escapedCardId === BYSTANDER_EXT_ID) {
        return 'bystander';
    }
    return undefined;
}
/**
 * Counts entries in the Escaped Villains pile whose card type is one of `cardTypes`.
 *
 * Uses an explicit `for...of` loop (no `.reduce()`), classifying each entry
 * via resolveEscapedPileEntryType so supply bystanders are counted too.
 *
 * @param gameState - The current game state (read-only).
 * @param cardTypes - The RevealedCardTypes to count.
 * @returns The number of matching entries in `G.escapedPile`.
 */
export function countEscapedPileByType(gameState, cardTypes) {
    let matchCount = 0;
    for (const escapedCardId of gameState.escapedPile) {
        const entryType = resolveEscapedPileEntryType(gameState, escapedCardId);
        if (entryType !== undefined && cardTypes.includes(entryType)) {
            matchCount = matchCount + 1;
        }
    }
    return matchCount;
}
/**
 * Counts entries in the Escaped Villains pile carrying a converted-villain origin.
 *
 * Reads `G.convertedVillainOrigins` (absent for non-converting schemes → 0). Uses
 * an explicit `for...of` loop (no `.reduce()`). This counts converted cards (e.g.
 * Killbots) DISTINCTLY from real villains — a converted card is typed `'villain'`
 * for routing, so `countEscapedPileByType(gs, ['villain'])` would wrongly include it.
 *
 * @param gameState - The current game state (read-only).
 * @param origin - The converted origin to count (e.g. 'killbot').
 * @returns The number of matching entries in `G.escapedPile`.
 */
export function countEscapedByConvertedOrigin(gameState, origin) {
    const origins = gameState.convertedVillainOrigins ?? {};
    let matchCount = 0;
    for (const escapedCardId of gameState.escapedPile) {
        if (origins[escapedCardId] === origin) {
            matchCount = matchCount + 1;
        }
    }
    return matchCount;
}
/**
 * Counts the Heroes a captor carried into the Escaped Villains pile (D-24657).
 *
 * A Hero card is one with a `G.cardTraits` entry (built from every registry Hero
 * instance); a converted-villain origin marks a converted Skrull Hero instead,
 * which `countEscapedByConvertedOrigin` counts, so those are skipped here and no
 * Hero is counted twice. Villains, Henchmen and Bystanders carry no trait entry.
 *
 * @param gameState - The current game state (read-only).
 * @returns The number of captured Heroes in `G.escapedPile`.
 */
export function countEscapedCapturedHeroes(gameState) {
    // why: map-level `??` — legacy / unit states predate WP-179 and leave the maps
    // undefined; a missing map means nothing matches rather than a throw.
    const traits = gameState.cardTraits ?? {};
    const origins = gameState.convertedVillainOrigins ?? {};
    let matchCount = 0;
    for (const escapedCardId of gameState.escapedPile) {
        if (traits[escapedCardId] !== undefined && origins[escapedCardId] === undefined) {
            matchCount = matchCount + 1;
        }
    }
    return matchCount;
}
/**
 * The count an `'escaped-converted-count'` loss compares with its threshold.
 *
 * The converted-origin count, plus the captured Heroes in the pile when the
 * condition sets `includeCapturedHeroes` (Secret Invasion, D-24738). The loss
 * check and the loss meter both read this, so they cannot disagree.
 *
 * @param gameState - The current game state (read-only).
 * @param condition - The escaped-converted-count condition.
 * @returns The number of counted entries in `G.escapedPile`.
 */
export function countEscapedConvertedLoss(gameState, condition) {
    const convertedCount = countEscapedByConvertedOrigin(gameState, condition.origin);
    if (condition.includeCapturedHeroes !== true) {
        return convertedCount;
    }
    return convertedCount + countEscapedCapturedHeroes(gameState);
}
/**
 * Applies the active scheme's escaped-pile resource-loss condition, if any.
 *
 * Handles both escape-pile loss kinds — `'escaped-pile-count'` (by card type,
 * D-24315) and `'escaped-converted-count'` (by converted origin, D-24325) — and
 * sets the SCHEME_LOSS counter to 1 (idempotent) + logs once when the escaped
 * pile holds at least `threshold` matching entries. A no-op for schemes without a
 * matching resourceLossCondition (including `'pile-depleted'`, handled by
 * applyPileDepletionResourceLoss), or when already lost. Never throws.
 *
 * @param gameState - The game state to mutate (counters + log only).
 */
export function applyEscapedPileResourceLoss(gameState) {
    const config = SCHEME_TWIST_CONFIGS.get(gameState.selection.schemeId);
    const condition = config?.resourceLossCondition;
    if (!condition ||
        (condition.kind !== 'escaped-pile-count' &&
            condition.kind !== 'escaped-converted-count')) {
        return;
    }
    // why: idempotent — once the scheme loss is latched, do not re-count or
    // re-log. evaluateEndgame treats SCHEME_LOSS >= 1 as the loss, so a single
    // set to 1 is sufficient and repeated escapes must not stack the counter.
    if ((gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] ?? 0) >= 1) {
        return;
    }
    // why: both kinds count the escaped pile — by card type, or by converted
    // origin (Killbots: converted cards are typed 'villain' for routing, so they
    // must be counted by origin, not by the shared 'villain' type).
    const matchCount = condition.kind === 'escaped-pile-count'
        ? countEscapedPileByType(gameState, condition.cardTypes)
        : countEscapedConvertedLoss(gameState, condition);
    if (matchCount >= condition.threshold) {
        // why: SCHEME_LOSS is set HERE, in the escape path, rather than derived
        // inside evaluateEndgame, because evaluateEndgame reads only G.counters
        // (the counter-only invariant). The escape path is the only place
        // G.escapedPile grows, so it is the correct place to evaluate the count.
        gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] = 1;
        pushLog(gameState, `Scheme loss triggered — ${describeEscapedLoss(gameState, condition, matchCount)} (threshold ${condition.threshold}).`);
    }
}
/**
 * Sets the scheme loss when the active scheme's `'scheme-counter'` tally reaches its threshold
 * (WP-832 / D-24765: Change the Outcome of WWII's conquered capitals).
 *
 * Idempotent (a latched loss is never re-counted or re-logged) and a no-op for any other
 * condition. Called by the scheme's own logic right after it raises the counter, the escape-path
 * precedent: evaluateEndgame reads only G.counters. Never throws.
 *
 * @param gameState - The game state to mutate (counters + log only).
 */
export function applySchemeCounterResourceLoss(gameState) {
    const condition = SCHEME_TWIST_CONFIGS.get(gameState.selection.schemeId)?.resourceLossCondition;
    if (condition === undefined || condition.kind !== 'scheme-counter') {
        return;
    }
    if ((gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] ?? 0) >= 1) {
        return;
    }
    const count = gameState.counters[condition.counter] ?? 0;
    if (count >= condition.threshold) {
        gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] = 1;
        pushLog(gameState, `Scheme loss triggered — ${count} ${condition.lossDescription} (threshold ${condition.threshold}).`);
    }
}
/**
 * Describes what reached an escaped-pile loss threshold, in the printed rule's terms.
 *
 * why: only Bystanders are "carried away by escaping Villains" (Midtown Bank Robbery).
 * Villains, Henchmen, Killbots and Skrulls escape themselves, so the generic "card(s)
 * carried away" line misdescribed three of the four escaped-pile schemes (match
 * 38KtA2vWDg9 logged "5 killbot card(s) carried away by escaping villains").
 *
 * @param gameState - The current game state (read-only), for the captured-Hero split.
 * @param condition - The escaped-pile resource-loss condition that triggered.
 * @param matchCount - How many matching cards are in the escaped pile.
 * @returns The phrase naming the count and what escaped, without the threshold.
 */
function describeEscapedLoss(gameState, condition, matchCount) {
    if (condition.kind === 'escaped-converted-count') {
        if (condition.origin === 'killbot') {
            return `${matchCount} Killbots escaped`;
        }
        // why: WP-823 / D-24756 — Brainwash the Military prints "When 5 S.H.I.E.L.D. Officers escape."
        if (condition.origin === 'shield-officer') {
            return `${matchCount} S.H.I.E.L.D. Officers escaped`;
        }
        // why: D-24738 — when captured Heroes count too, name both parts so the line
        // matches the printed "6 Heroes" and the player can see where the count came from.
        const capturedCount = condition.includeCapturedHeroes === true ? countEscapedCapturedHeroes(gameState) : 0;
        if (capturedCount > 0) {
            return `${matchCount} Heroes escaped (${matchCount - capturedCount} Skrulls, ${capturedCount} captured)`;
        }
        return `${matchCount} Skrulls escaped`;
    }
    if (condition.cardTypes.length === 1 && condition.cardTypes[0] === 'bystander') {
        return `${matchCount} Bystanders carried away by escaping Villains`;
    }
    return `${matchCount} Villains escaped`;
}
/**
 * Lists the piles a `pile-depleted` condition names, in declaration order.
 *
 * why (D-24595): the condition is an exclusive union — a single `pile` (every
 * core entry) or a `piles` list ("the Hero Deck or Villain Deck runs out"). This
 * one normaliser is shared by the loss rule, the setup capture and the meter, so
 * no caller re-decides which form it is looking at.
 *
 * @param condition - A `pile-depleted` resource-loss condition.
 * @returns The named piles (one entry for the single-pile form).
 */
export function listConditionPiles(condition) {
    if (condition.piles !== undefined) {
        return condition.piles;
    }
    return [condition.pile];
}
/**
 * Returns the number of cards remaining in a named depletion-loss pile.
 *
 * Maps a `pile-depleted` condition's pile name to the length of the
 * corresponding zone in `G`. `'heroDeck'` → `G.heroDeck` (Super Hero Civil War,
 * WP-510); `'wounds'` → `G.piles.wounds` (Legacy Virus, WP-511);
 * `'villainDeck'` → `G.villainDeck.deck` (WP-763 / D-24595).
 *
 * Exported so `schemeLossProgress.ts` reads the SAME pile mapping the loss
 * itself turns on (WP-562). A second mapping there would be free to drift from
 * this one, which is exactly the class of defect that module exists to prevent.
 *
 * @param gameState - The current game state (read-only).
 * @param pile - The pile name from the resourceLossCondition.
 * @returns The remaining card count in that pile.
 */
export function remainingPileCount(gameState, pile) {
    // why: an explicit switch (not dynamic G[pile] indexing) so each supported
    // pile maps to its real zone location — the hero deck lives at G.heroDeck,
    // the wound stack under G.piles.wounds, the Villain Deck under
    // G.villainDeck.deck.
    switch (pile) {
        case 'heroDeck':
            return gameState.heroDeck.length;
        case 'wounds':
            return gameState.piles.wounds.length;
        case 'villainDeck':
            return gameState.villainDeck.deck.length;
        case 'monsterPit':
            // why: D-24736 — only Clash names the Monster Pit, and Clash setup always
            // builds it; an absent Pit reads as empty.
            return gameState.monsterPit?.length ?? 0;
    }
}
/**
 * Applies the active scheme's pile-depletion resource-loss condition, if any.
 *
 * If the active scheme declares a `'pile-depleted'` resourceLossCondition and
 * ANY named pile is empty (`remainingPileCount === 0`), sets the SCHEME_LOSS
 * counter to 1 (idempotent) and logs once. A no-op for schemes without a
 * `'pile-depleted'` condition, or when already lost. Never throws.
 *
 * @param gameState - The game state to mutate (counters + log only).
 */
export function applyPileDepletionResourceLoss(gameState) {
    const config = SCHEME_TWIST_CONFIGS.get(gameState.selection.schemeId);
    const condition = config?.resourceLossCondition;
    if (!condition || condition.kind !== 'pile-depleted') {
        return;
    }
    // why: idempotent — once the scheme loss is latched, do not re-check or
    // re-log. evaluateEndgame treats SCHEME_LOSS >= 1 as the loss, so a single
    // set to 1 is sufficient.
    if ((gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] ?? 0) >= 1) {
        return;
    }
    for (const pile of listConditionPiles(condition)) {
        if (remainingPileCount(gameState, pile) === 0) {
            // why: SCHEME_LOSS is set HERE (called from the play-phase turn.onMove
            // hook, a central per-move chokepoint) rather than at the recruitHero
            // refill, because the named pile can be drained by paths other than a
            // recruit — Super Hero Civil War's ko-from-hq twist forces HQ refills that
            // drain G.heroDeck outside any recruit move, and the Villain Deck drains on
            // every reveal. evaluateEndgame stays counter-only; the depletion decision
            // lives here at the check site. Because onMove runs the final-turn latch
            // first and evaluateEndgame checks SCHEME_LOSS before the deck-out tie
            // (D-24319), a Villain Deck runout on a scheme that names it is a loss,
            // not a tie.
            gameState.counters[ENDGAME_CONDITIONS.SCHEME_LOSS] = 1;
            pushLog(gameState, `Scheme loss triggered — the ${pileDisplayName(pile)} has run out.`);
            return;
        }
    }
}
/**
 * Reports whether the active scheme's Evil Wins includes a named pile running out.
 *
 * @param gameState - The current game state (read-only).
 * @param pile - The pile to ask about.
 * @returns True when the scheme's `pile-depleted` condition names that pile.
 */
export function isSchemeLossPile(gameState, pile) {
    const config = SCHEME_TWIST_CONFIGS.get(gameState.selection.schemeId);
    const condition = config?.resourceLossCondition;
    if (!condition || condition.kind !== 'pile-depleted') {
        return false;
    }
    return listConditionPiles(condition).includes(pile);
}
/**
 * Names a depletion pile the way the printed rules do, for the game log.
 *
 * @param pile - The depletion pile.
 * @returns The rulebook name ("Villain Deck", "Hero Deck", "Wound Stack").
 */
function pileDisplayName(pile) {
    // why (D-24599): the loss line printed the internal key ("the villainDeck pile
    // has run out"). An exhaustive switch, so a new pile fails to compile here
    // rather than leaking its identifier into the player's log.
    switch (pile) {
        case 'heroDeck':
            return 'Hero Deck';
        case 'wounds':
            return 'Wound Stack';
        case 'villainDeck':
            return 'Villain Deck';
        case 'monsterPit':
            return 'Monster Pit';
    }
}
//# sourceMappingURL=schemeResourceLoss.js.map